// Аптечка и гарантии: вещи со сроком. Список по срокам, добавить / изменить / убрать, фото чека или упаковки.
import { addMonths, endOfMonth, type HomeItemDto, type HomeItemKind } from "@home/shared";
import { useState } from "react";
import { api, fileUrl } from "../api.ts";
import { todayIso } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText } from "../meta.ts";
import { compressImage } from "../photo.ts";
import { useSync } from "../store.ts";
import { confirmDialog, hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";
import { EmptyState } from "./EmptyState.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

/** Сроки гарантии на кнопках, месяцев. */
const WARRANTY_TERMS = [6, 12, 24, 36, 60];
const MONTHS_RU = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const MONTHS_UZ = ["Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun", "Iyul", "Avgust", "Sentabr", "Oktabr", "Noyabr", "Dekabr"];

/** «12.03.2027». */
function fullDate(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

/** Срок годности — месяц и год, как на упаковке: «05.2027»; гарантия — точная дата. */
function dateText(item: Pick<HomeItemDto, "kind" | "expiresOn">): string {
  return item.kind === "medicine" ? `${item.expiresOn.slice(5, 7)}.${item.expiresOn.slice(0, 4)}` : fullDate(item.expiresOn);
}

function termLabel(months: number): string {
  return months % 12 === 0 ? t("warrantyYears", { n: months / 12 }) : t("warrantyMonths", { n: months });
}

/** Пометка справа: просрочено / через N дней / ещё N мес. (гарантия). */
function TermBadge({ item }: { item: HomeItemDto }) {
  if (item.status === "expired") {
    return <span className="term bad">{item.kind === "medicine" ? t("expiredMedicine") : t("expiredWarranty")}</span>;
  }
  if (item.status === "soon") {
    return <span className="term warn">{item.daysLeft === 0 ? t("todayLast") : t("inDaysN", { n: item.daysLeft })}</span>;
  }
  if (item.kind === "warranty") return <span className="term">{t("monthsLeftN", { n: Math.max(1, Math.round(item.daysLeft / 30.4)) })}</span>;
  return null;
}

function ItemRows({ items, onPick }: { items: HomeItemDto[]; onPick: (item: HomeItemDto) => void }) {
  return (
    <ul className="row-list">
      {items.map((item) => {
        const icon = item.status === "expired" ? "⚠️" : item.status === "soon" ? "⏳" : item.kind === "medicine" ? "💊" : "🛡";
        const meta = [t("until", { date: dateText(item) }), item.note, item.photoUrl ? "📷" : null].filter(Boolean).join(" · ");
        return (
          <li key={item.id}>
            <button
              type="button"
              className={item.status === "expired" && item.kind === "warranty" ? "row row-btn faded" : "row row-btn"}
              onClick={() => {
                hapticSelect();
                onPick(item);
              }}
            >
              <span className="row-icon" aria-hidden="true">
                {icon}
              </span>
              <div className="row-body">
                <div className="row-title">{item.name}</div>
                <div className="row-meta">{meta}</div>
              </div>
              <div className="row-end">
                <TermBadge item={item} />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** «Аптечка» или «Гарантии» из «Ещё»: список по срокам и «Добавить». */
export function HomeItemsSheet({ kind, lang, onClose }: { kind: HomeItemKind; lang: Lang; onClose: () => void }) {
  const remote = useRemote(`home-items:${kind}`, () => api.homeItems(kind));
  const [editing, setEditing] = useState<HomeItemDto | "new" | null>(null);
  const items = remote.data?.items ?? [];
  const medicine = kind === "medicine";
  // Гарантии: действующие сверху, закончившиеся — отдельно внизу. Лекарства: просроченные первыми — их пора выбросить.
  const active = medicine ? items : items.filter((i) => i.status !== "expired");
  const expired = medicine ? [] : items.filter((i) => i.status === "expired").reverse();

  return (
    <Sheet title={medicine ? `💊 ${t("kitTitle")}` : `🛡 ${t("warrantiesTitle")}`} onClose={onClose}>
      {!remote.data && remote.loading ? (
        <div className="skeleton skeleton-block" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={medicine ? "💊" : "🛡"}
          title={medicine ? t("kitEmpty") : t("warrantiesEmpty")}
          text={medicine ? t("kitEmptyHint") : t("warrantiesEmptyHint")}
        />
      ) : (
        <>
          <ItemRows items={active} onPick={setEditing} />
          {expired.length > 0 && (
            <>
              <div className="section-title">{t("expiredWarranties")}</div>
              <ItemRows items={expired} onPick={setEditing} />
            </>
          )}
        </>
      )}
      <PrimaryAction label={`＋ ${medicine ? t("addMedicine") : t("addWarranty")}`} onClick={() => setEditing("new")} />
      {editing && <HomeItemForm kind={kind} item={editing === "new" ? null : editing} lang={lang} onClose={() => setEditing(null)} />}
    </Sheet>
  );
}

/** Новая вещь или правка: лекарство — месяц и год с упаковки; гарантия — дата покупки и срок. */
function HomeItemForm({ kind, item, lang, onClose }: { kind: HomeItemKind; item: HomeItemDto | null; lang: Lang; onClose: () => void }) {
  const { me } = useSync();
  const medicine = kind === "medicine";
  const today = todayIso();
  const thisYear = Number(today.slice(0, 4));
  const [name, setName] = useState(item?.name ?? "");
  const [note, setNote] = useState(item?.note ?? "");
  // Лекарство: по умолчанию — через год, тот же месяц.
  const [month, setMonth] = useState(item ? Number(item.expiresOn.slice(5, 7)) : Number(today.slice(5, 7)));
  const [year, setYear] = useState(item ? Number(item.expiresOn.slice(0, 4)) : thisYear + 1);
  // Гарантия: дата покупки и срок кнопкой или своя дата окончания.
  // У старой гарантии даты покупки может не быть — не подставляем «сегодня», срок остаётся своей датой.
  const boughtInit = item ? (item.boughtOn ?? "") : today;
  const termInit = item ? (boughtInit ? (WARRANTY_TERMS.find((m) => addMonths(boughtInit, m) === item.expiresOn) ?? null) : null) : 12;
  const [boughtOn, setBoughtOn] = useState(boughtInit);
  const [term, setTerm] = useState<number | null>(termInit);
  const [until, setUntil] = useState(item?.expiresOn ?? "");
  const [fileId, setFileId] = useState<string | null>(item?.photoFileId ?? null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expiresOn = medicine ? endOfMonth(year, month) : term !== null ? (boughtOn ? addMonths(boughtOn, term) : "") : until;
  const photoUrl = fileId ? (fileId === item?.photoFileId && item.photoUrl ? item.photoUrl : me ? fileUrl(fileId, me.fileToken) : null) : null;
  const years = Array.from({ length: 8 }, (_, i) => thisYear - 2 + i);

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const { id } = await api.uploadPhoto(await compressImage(file));
      setFileId(id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (busy || uploading) return;
    if (!name.trim()) return setError(t("fillName"));
    if (!medicine && term !== null && !boughtOn) return setError(t("fillBoughtOn"));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(expiresOn)) return setError(t("fillUntil"));
    setBusy(true);
    setError(null);
    const body = { kind, name: name.trim(), expiresOn, boughtOn: medicine ? null : boughtOn || null, note: note.trim() || null, fileId };
    try {
      if (item) await api.updateHomeItem(item.id, body);
      else await api.createHomeItem(body);
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item || busy) return;
    if (!(await confirmDialog(t(medicine ? "confirmUsedUp" : "confirmDeleteWarranty", { name: item.name }), medicine ? t("usedUp") : t("delete")))) return;
    setBusy(true);
    try {
      await api.deleteHomeItem(item.id);
      hapticTap();
      toast(t("deletedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  const title = item ? item.name : medicine ? t("addMedicine") : t("addWarranty");
  return (
    <Sheet title={`${medicine ? "💊" : "🛡"} ${title}`} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span>{medicine ? t("medicineName") : t("warrantyName")}</span>
          <input
            className="input"
            placeholder={medicine ? t("medicineNamePh") : t("warrantyNamePh")}
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        {medicine ? (
          <div className="field">
            <span>{t("expiresMonth")}</span>
            <div className="field-row">
              <select className="input" value={month} onChange={(e) => setMonth(Number(e.target.value))} aria-label={t("expiresMonth")}>
                {(lang === "uz" ? MONTHS_UZ : MONTHS_RU).map((m, i) => (
                  <option key={m} value={i + 1}>
                    {String(i + 1).padStart(2, "0")} · {m}
                  </option>
                ))}
              </select>
              <select className="input" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t("expiresMonth")}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <>
            <label className="field">
              <span>{t("boughtOnL")}</span>
              <input className="input" type="date" value={boughtOn} max={today} onChange={(e) => setBoughtOn(e.target.value)} />
            </label>
            <div className="field">
              <span>{t("warrantyFor")}</span>
              <div className="chips" role="radiogroup" aria-label={t("warrantyFor")}>
                {[...WARRANTY_TERMS, null].map((m) => (
                  <button
                    key={m ?? "other"}
                    type="button"
                    role="radio"
                    aria-checked={term === m}
                    className={term === m ? "chip active" : "chip"}
                    onClick={() => {
                      hapticSelect();
                      setTerm(m);
                      if (m === null && !until) setUntil(addMonths(boughtOn || today, 12));
                    }}
                  >
                    {m === null ? t("warrantyOther") : termLabel(m)}
                  </button>
                ))}
              </div>
            </div>
            {term === null && (
              <label className="field">
                <span>{t("untilDate")}</span>
                <input className="input" type="date" value={until} min={boughtOn || undefined} onChange={(e) => setUntil(e.target.value)} />
              </label>
            )}
            {/^\d{4}-\d{2}-\d{2}$/.test(expiresOn) && <p className="hint">{t("warrantyUntil", { date: fullDate(expiresOn) })}</p>}
          </>
        )}

        <label className="field">
          <span>{medicine ? t("medicineNote") : t("warrantyNote")}</span>
          <input
            className="input"
            placeholder={medicine ? t("medicineNotePh") : t("warrantyNotePh")}
            value={note}
            maxLength={200}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <div className="field">
          <span>{medicine ? t("packPhoto") : t("warrantyPhoto")}</span>
          {photoUrl && (
            <a href={photoUrl} target="_blank" rel="noreferrer" aria-label={t("photoTap")}>
              <img className="photo-preview" src={photoUrl} alt="" />
            </a>
          )}
          <div className="photo-actions">
            <label className={uploading ? "btn disabled" : "btn"}>
              📷 {uploading ? t("uploading") : t("addPhoto")}
              <input type="file" accept="image/*" hidden disabled={uploading} onChange={(e) => void onPhoto(e.target.files?.[0])} />
            </label>
            {fileId && (
              <button type="button" className="btn" onClick={() => setFileId(null)}>
                {t("removePhoto")}
              </button>
            )}
          </div>
        </div>

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {item && (
          <button type="button" className="btn danger btn-block" disabled={busy} onClick={() => void remove()}>
            {medicine ? `🗑 ${t("usedUp")}` : `🗑 ${t("delete")}`}
          </button>
        )}
        <PrimaryAction label={t("save")} onClick={() => void submit()} busy={busy || uploading} />
      </div>
    </Sheet>
  );
}
